/**
 * SFD Custom Themes
 *
 * Defined here and injected into epx-flow via epxFlow.registerTheme(). The
 * same theme object is also forwarded to epxDmn.registerTheme() so the DMN
 * editor visually tracks the BPMN palette. The optional `dmn` block carries
 * DMN-specific colors (DRG nodes, requirement edges, accent, table tints);
 * if absent, DMN falls back to its built-in palette.
 *
 * epx-flow core (default, dark, highContrast) is not modified.
 *
 * Usage in initModeler:
 *   for (const [name, theme] of Object.entries(SFD_THEMES)) {
 *     instance.registerTheme(name, theme);
 *   }
 */

export const SFD_THEMES = {

  // ─── Nord ─────────────────────────────────────────────────────────
  // Arctic color palette — cool icy blues and greens on snow-white canvas
  nord: {
    startEvent:              { stroke: '#a3be8c', fill: '#eceff4', strokeWidth: 1.5 },
    endEvent:                { stroke: '#bf616a', fill: '#f5e6e8', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#5e81ac', fill: '#e5eaf2', strokeWidth: 1 },
    intermediateThrowEvent:  { stroke: '#5e81ac', fill: '#e5eaf2', strokeWidth: 1 },
    boundaryEvent:           { stroke: '#d08770', fill: '#f5ece8', strokeWidth: 1.5 },
    userTask:         { stroke: '#5e81ac', fill: '#dce5f0', strokeWidth: 1 },
    serviceTask:      { stroke: '#b48ead', fill: '#ede5ed', strokeWidth: 1 },
    scriptTask:       { stroke: '#d08770', fill: '#f3e9e4', strokeWidth: 1 },
    manualTask:       { stroke: '#88c0d0', fill: '#daeef3', strokeWidth: 1 },
    sendTask:         { stroke: '#81a1c1', fill: '#e0e8f2', strokeWidth: 1 },
    receiveTask:      { stroke: '#8fbcbb', fill: '#dceeed', strokeWidth: 1 },
    businessRuleTask: { stroke: '#ebcb8b', fill: '#faf5e6', strokeWidth: 1 },
    callActivity:     { stroke: '#a3be8c', fill: '#e9f2e3', strokeWidth: 3 },
    exclusiveGateway:  { stroke: '#ebcb8b', fill: '#faf5e6', strokeWidth: 2 },
    parallelGateway:   { stroke: '#88c0d0', fill: '#daeef3', strokeWidth: 2 },
    inclusiveGateway:  { stroke: '#b48ead', fill: '#ede5ed', strokeWidth: 2 },
    complexGateway:    { stroke: '#4c566a', fill: '#eceff4', strokeWidth: 2 },
    eventBasedGateway: { stroke: '#a3be8c', fill: '#e9f2e3', strokeWidth: 2 },
    subProcess:   { stroke: '#5e81ac', fill: '#dce5f0', strokeWidth: 2 },
    transaction:  { stroke: '#5e81ac', fill: '#dce5f0', strokeWidth: 2 },
    participant:  { stroke: '#3b4252', fill: '#e5e9f0', strokeWidth: 2 },
    lane:         { stroke: '#4c566a', fill: '#eceff4', strokeWidth: 1 },
    dataObjectReference: { stroke: '#4c566a', fill: '#eceff4', strokeWidth: 1.5 },
    dataStoreReference:  { stroke: '#4c566a', fill: '#eceff4', strokeWidth: 1.5 },
    group:               { stroke: '#81a1c1', fill: 'none',    strokeWidth: 1.5 },
    textAnnotation:      { stroke: '#4c566a', fill: 'none',    strokeWidth: 1.5 },
    sequenceFlow:    { stroke: '#4c566a', strokeWidth: 1.5 },
    messageFlow:     { stroke: '#5e81ac', strokeWidth: 1.5 },
    association:     { stroke: '#81a1c1', strokeWidth: 1 },
    dataAssociation: { stroke: '#81a1c1', strokeWidth: 1 },
    grid:   { color: '#c8d4e0' },
    canvas: { background: '#ffffff' },
    text:   { fill: '#2e3440' },
    panel: {
      background: '#e5e9f0',       headerBackground: '#eceff4',
      headerBorder: '#d8dee9',     text: '#2e3440',
      textSecondary: '#3b4252',    textMuted: '#4c566a',
      border: '#d8dee9',           inputBackground: '#eceff4',
      inputBorder: '#81a1c1',      hoverBackground: '#dce5ef',
      accentBackground: '#dce5f0', accentBorder: '#81a1c1',
      accentText: '#5e81ac',
    },
    dmn: {
      decision:               { stroke: '#5e81ac', fill: '#dce5f0', strokeWidth: 2 },
      inputData:              { stroke: '#88c0d0', fill: '#daeef3', strokeWidth: 2 },
      businessKnowledgeModel: { stroke: '#b48ead', fill: '#ede5ed', strokeWidth: 2 },
      knowledgeSource:        { stroke: '#d08770', fill: '#f3e9e4', strokeWidth: 2 },
      decisionService:        { stroke: '#5e81ac', fill: '#dce5f0', strokeWidth: 2 },
      textAnnotation:         { stroke: '#4c566a', fill: 'none',    strokeWidth: 1.5 },
      informationRequirement: { stroke: '#5e81ac', strokeWidth: 1.5 },
      knowledgeRequirement:   { stroke: '#b48ead', strokeWidth: 1.5 },
      authorityRequirement:   { stroke: '#d08770', strokeWidth: 1.5 },
      association:            { stroke: '#81a1c1', strokeWidth: 1 },
      accent: { base: '#5e81ac', hover: '#3b6193', bg: '#dce5f0', border: '#81a1c1', alpha: 'rgba(94,129,172,0.25)' },
      feelSyntax: {
        keyword: '#5e81ac',
        literal: '#5e81ac',
        number:  '#d08770',
        string:  '#a3be8c',
        op:      '#bf616a',
        builtin: '#b48ead',
        punct:   '#4c566a',
      },
      chips: {
        accentBgSoft:    'rgba(94, 129, 172, 0.12)',
        accentText:      '#5e81ac',
        accentSoftBg:    'rgba(94, 129, 172, 0.15)',
        accentSoftBorder:'rgba(94, 129, 172, 0.45)',
        unknownStroke:   '#bf616a',
        unknownSoft:     'rgba(191, 97, 106, 0.12)',
        unknownText:     '#bf616a',
      },
      tableGrid: {
        border: '#d8dee9', headerBg: '#eceff4', indexBg: '#e5e9f0', zebra: '#eceff4',
        inputBg:   'rgba(136, 192, 208, 0.18)',
        inputCell: 'rgba(136, 192, 208, 0.04)',
        outputBg:  'rgba(208, 135, 112, 0.18)',
        outputCell:'rgba(208, 135, 112, 0.04)',
        inputBorder: '#81a1c1', inputBgField: '#fff', focusBg: '#fff9c4',
      },
    },
  },

  // ─── Ocean Dark ───────────────────────────────────────────────────
  // Deep ocean blues and teals — mysterious and modern
  ocean: {
    startEvent:              { stroke: '#00d4aa', fill: '#001f1a', strokeWidth: 1.5 },
    endEvent:                { stroke: '#ff6b6b', fill: '#1f0a0a', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 1 },
    intermediateThrowEvent:  { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 1 },
    boundaryEvent:           { stroke: '#ff9f43', fill: '#1f1200', strokeWidth: 1.5 },
    userTask:         { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 1 },
    serviceTask:      { stroke: '#a29bfe', fill: '#0d0a20', strokeWidth: 1 },
    scriptTask:       { stroke: '#ff9f43', fill: '#1f1200', strokeWidth: 1 },
    manualTask:       { stroke: '#00d4aa', fill: '#001f1a', strokeWidth: 1 },
    sendTask:         { stroke: '#74b9ff', fill: '#001323', strokeWidth: 1 },
    receiveTask:      { stroke: '#55efc4', fill: '#001f16', strokeWidth: 1 },
    businessRuleTask: { stroke: '#fdcb6e', fill: '#1f1800', strokeWidth: 1 },
    callActivity:     { stroke: '#00d4aa', fill: '#001f1a', strokeWidth: 3 },
    exclusiveGateway:  { stroke: '#fdcb6e', fill: '#1f1800', strokeWidth: 2 },
    parallelGateway:   { stroke: '#00d4aa', fill: '#001f1a', strokeWidth: 2 },
    inclusiveGateway:  { stroke: '#fd79a8', fill: '#1f0016', strokeWidth: 2 },
    complexGateway:    { stroke: '#636e72', fill: '#0d1117', strokeWidth: 2 },
    eventBasedGateway: { stroke: '#55efc4', fill: '#001f16', strokeWidth: 2 },
    subProcess:   { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 2 },
    transaction:  { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 2 },
    participant:  { stroke: '#2d9cdb', fill: '#0a1628', strokeWidth: 2 },
    lane:         { stroke: '#1a3a5c', fill: '#0d1f36', strokeWidth: 1 },
    dataObjectReference: { stroke: '#636e72', fill: '#0a1628', strokeWidth: 1.5 },
    dataStoreReference:  { stroke: '#636e72', fill: '#0a1628', strokeWidth: 1.5 },
    group:               { stroke: '#2d9cdb', fill: 'none',    strokeWidth: 1.5 },
    textAnnotation:      { stroke: '#636e72', fill: 'none',    strokeWidth: 1.5 },
    sequenceFlow:    { stroke: '#2d9cdb', strokeWidth: 1.5 },
    messageFlow:     { stroke: '#4fb3ff', strokeWidth: 1.5 },
    association:     { stroke: '#1a3a5c', strokeWidth: 1 },
    dataAssociation: { stroke: '#1a3a5c', strokeWidth: 1 },
    grid:   { color: '#142e4e' },
    canvas: { background: '#0a1628' },
    text:   { fill: '#c8d8f0' },
    panel: {
      background: '#0d1f36',       headerBackground: '#0a1628',
      headerBorder: '#1a3a5c',     text: '#c8d8f0',
      textSecondary: '#8ab4d8',    textMuted: '#4a7a9b',
      border: '#1a3a5c',           inputBackground: '#0f2744',
      inputBorder: '#2d5a84',      hoverBackground: '#142c4a',
      accentBackground: '#001829', accentBorder: '#2d9cdb',
      accentText: '#4fb3ff',
    },
    dmn: {
      decision:               { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 2 },
      inputData:              { stroke: '#00d4aa', fill: '#001f1a', strokeWidth: 2 },
      businessKnowledgeModel: { stroke: '#a29bfe', fill: '#0d0a20', strokeWidth: 2 },
      knowledgeSource:        { stroke: '#fdcb6e', fill: '#1f1800', strokeWidth: 2 },
      decisionService:        { stroke: '#4fb3ff', fill: '#001829', strokeWidth: 2 },
      textAnnotation:         { stroke: '#8ab4d8', fill: 'none',    strokeWidth: 1.5 },
      informationRequirement: { stroke: '#4fb3ff', strokeWidth: 1.5 },
      knowledgeRequirement:   { stroke: '#a29bfe', strokeWidth: 1.5 },
      authorityRequirement:   { stroke: '#fdcb6e', strokeWidth: 1.5 },
      association:            { stroke: '#1a3a5c', strokeWidth: 1 },
      accent: { base: '#4fb3ff', hover: '#22a4e0', bg: '#001829', border: '#2d9cdb', alpha: 'rgba(79,179,255,0.25)' },
      feelSyntax: {
        keyword: '#7ec5ff',
        literal: '#7ec5ff',
        number:  '#a8c5e0',
        string:  '#9eddc9',
        op:      '#5fb3d4',
        builtin: '#b8a8fa',
        punct:   '#4a6b88',
      },
      chips: {
        accentBgSoft:    'rgba(79, 179, 255, 0.15)',
        accentText:      '#cfe6ff',
        accentSoftBg:    'rgba(79, 179, 255, 0.18)',
        accentSoftBorder:'rgba(79, 179, 255, 0.45)',
        unknownStroke:   '#f87171',
        unknownSoft:     'rgba(248, 113, 113, 0.18)',
        unknownText:     '#fca5a5',
      },
      tableGrid: {
        border: '#1a3a5c', headerBg: '#0a1628', indexBg: '#0d1f36', zebra: '#0f2440',
        inputBg:   'rgba(0, 212, 170, 0.18)',
        inputCell: 'rgba(0, 212, 170, 0.04)',
        outputBg:  'rgba(253, 203, 110, 0.18)',
        outputCell:'rgba(253, 203, 110, 0.04)',
        inputBorder: '#2d5a84', inputBgField: '#0f2744', focusBg: '#3b3107',
      },
    },
  },

  // ─── Dracula ──────────────────────────────────────────────────────
  // The iconic Dracula dark theme — purples, pinks, and electric cyans
  dracula: {
    startEvent:              { stroke: '#50fa7b', fill: '#1a2a1e', strokeWidth: 1.5 },
    endEvent:                { stroke: '#ff5555', fill: '#2a1515', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#8be9fd', fill: '#152129', strokeWidth: 1 },
    intermediateThrowEvent:  { stroke: '#8be9fd', fill: '#152129', strokeWidth: 1 },
    boundaryEvent:           { stroke: '#ffb86c', fill: '#29200d', strokeWidth: 1.5 },
    userTask:         { stroke: '#8be9fd', fill: '#152129', strokeWidth: 1 },
    serviceTask:      { stroke: '#bd93f9', fill: '#1d1429', strokeWidth: 1 },
    scriptTask:       { stroke: '#ffb86c', fill: '#29200d', strokeWidth: 1 },
    manualTask:       { stroke: '#50fa7b', fill: '#1a2a1e', strokeWidth: 1 },
    sendTask:         { stroke: '#6272a4', fill: '#131829', strokeWidth: 1 },
    receiveTask:      { stroke: '#8be9fd', fill: '#152129', strokeWidth: 1 },
    businessRuleTask: { stroke: '#f1fa8c', fill: '#29290d', strokeWidth: 1 },
    callActivity:     { stroke: '#50fa7b', fill: '#1a2a1e', strokeWidth: 3 },
    exclusiveGateway:  { stroke: '#f1fa8c', fill: '#29290d', strokeWidth: 2 },
    parallelGateway:   { stroke: '#50fa7b', fill: '#1a2a1e', strokeWidth: 2 },
    inclusiveGateway:  { stroke: '#ff79c6', fill: '#2a1225', strokeWidth: 2 },
    complexGateway:    { stroke: '#6272a4', fill: '#282a36', strokeWidth: 2 },
    eventBasedGateway: { stroke: '#bd93f9', fill: '#1d1429', strokeWidth: 2 },
    subProcess:   { stroke: '#6272a4', fill: '#282a36', strokeWidth: 2 },
    transaction:  { stroke: '#6272a4', fill: '#282a36', strokeWidth: 2 },
    participant:  { stroke: '#44475a', fill: '#21222c', strokeWidth: 2 },
    lane:         { stroke: '#44475a', fill: '#282a36', strokeWidth: 1 },
    dataObjectReference: { stroke: '#6272a4', fill: '#21222c', strokeWidth: 1.5 },
    dataStoreReference:  { stroke: '#6272a4', fill: '#21222c', strokeWidth: 1.5 },
    group:               { stroke: '#44475a', fill: 'none',    strokeWidth: 1.5 },
    textAnnotation:      { stroke: '#6272a4', fill: 'none',    strokeWidth: 1.5 },
    sequenceFlow:    { stroke: '#6272a4', strokeWidth: 1.5 },
    messageFlow:     { stroke: '#8be9fd', strokeWidth: 1.5 },
    association:     { stroke: '#44475a', strokeWidth: 1 },
    dataAssociation: { stroke: '#44475a', strokeWidth: 1 },
    grid:   { color: '#3d3f52' },
    canvas: { background: '#282a36' },
    text:   { fill: '#f8f8f2' },
    panel: {
      background: '#21222c',       headerBackground: '#282a36',
      headerBorder: '#44475a',     text: '#f8f8f2',
      textSecondary: '#cdd6f4',    textMuted: '#6272a4',
      border: '#44475a',           inputBackground: '#282a36',
      inputBorder: '#6272a4',      hoverBackground: '#2e3044',
      accentBackground: '#1d1429', accentBorder: '#bd93f9',
      accentText: '#bd93f9',
    },
    dmn: {
      decision:               { stroke: '#8be9fd', fill: '#152129', strokeWidth: 2 },
      inputData:              { stroke: '#50fa7b', fill: '#1a2a1e', strokeWidth: 2 },
      businessKnowledgeModel: { stroke: '#bd93f9', fill: '#1d1429', strokeWidth: 2 },
      knowledgeSource:        { stroke: '#f1fa8c', fill: '#29290d', strokeWidth: 2 },
      decisionService:        { stroke: '#8be9fd', fill: '#152129', strokeWidth: 2 },
      textAnnotation:         { stroke: '#6272a4', fill: 'none',    strokeWidth: 1.5 },
      informationRequirement: { stroke: '#8be9fd', strokeWidth: 1.5 },
      knowledgeRequirement:   { stroke: '#bd93f9', strokeWidth: 1.5 },
      authorityRequirement:   { stroke: '#f1fa8c', strokeWidth: 1.5 },
      association:            { stroke: '#44475a', strokeWidth: 1 },
      accent: { base: '#bd93f9', hover: '#9d6df5', bg: '#1d1429', border: '#bd93f9', alpha: 'rgba(189,147,249,0.25)' },
      feelSyntax: {
        keyword: '#8be9fd',
        literal: '#8be9fd',
        number:  '#bd93f9',
        string:  '#a8e6a3',
        op:      '#ff79c6',
        builtin: '#f1fa8c',
        punct:   '#6272a4',
      },
      chips: {
        accentBgSoft:    'rgba(139, 233, 253, 0.15)',
        accentText:      '#bff5ff',
        accentSoftBg:    'rgba(139, 233, 253, 0.18)',
        accentSoftBorder:'rgba(139, 233, 253, 0.45)',
        unknownStroke:   '#ff5555',
        unknownSoft:     'rgba(255, 85, 85, 0.18)',
        unknownText:     '#ff8a8a',
      },
      tableGrid: {
        border: '#44475a', headerBg: '#21222c', indexBg: '#1a1b22', zebra: '#1f2027',
        inputBg:   'rgba(80, 250, 123, 0.18)',
        inputCell: 'rgba(80, 250, 123, 0.04)',
        outputBg:  'rgba(241, 250, 140, 0.18)',
        outputCell:'rgba(241, 250, 140, 0.04)',
        inputBorder: '#6272a4', inputBgField: '#282a36', focusBg: '#3b3107',
      },
    },
  },

  // ─── Sunset ───────────────────────────────────────────────────────
  // Warm sunset tones — corals, golds, and soft purples on cream
  sunset: {
    startEvent:              { stroke: '#2ec4b6', fill: '#e8faf8', strokeWidth: 1.5 },
    endEvent:                { stroke: '#e63946', fill: '#fde8ea', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 1 },
    intermediateThrowEvent:  { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 1 },
    boundaryEvent:           { stroke: '#e07a5f', fill: '#fceee9', strokeWidth: 1.5 },
    userTask:         { stroke: '#e07a5f', fill: '#fceee9', strokeWidth: 1 },
    serviceTask:      { stroke: '#9c6b98', fill: '#f5eaf5', strokeWidth: 1 },
    scriptTask:       { stroke: '#f4a261', fill: '#fef3e8', strokeWidth: 1 },
    manualTask:       { stroke: '#2ec4b6', fill: '#e8faf8', strokeWidth: 1 },
    sendTask:         { stroke: '#e76f51', fill: '#fde9e3', strokeWidth: 1 },
    receiveTask:      { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 1 },
    businessRuleTask: { stroke: '#e9c46a', fill: '#fefaec', strokeWidth: 1 },
    callActivity:     { stroke: '#2a9d8f', fill: '#e4f5f3', strokeWidth: 3 },
    exclusiveGateway:  { stroke: '#e9c46a', fill: '#fefaec', strokeWidth: 2 },
    parallelGateway:   { stroke: '#2ec4b6', fill: '#e8faf8', strokeWidth: 2 },
    inclusiveGateway:  { stroke: '#c77dff', fill: '#f5e8ff', strokeWidth: 2 },
    complexGateway:    { stroke: '#6c757d', fill: '#f8f9fa', strokeWidth: 2 },
    eventBasedGateway: { stroke: '#2a9d8f', fill: '#e4f5f3', strokeWidth: 2 },
    subProcess:   { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 2 },
    transaction:  { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 2 },
    participant:  { stroke: '#3d405b', fill: '#f4f1de', strokeWidth: 2 },
    lane:         { stroke: '#8d99ae', fill: '#faf9f6', strokeWidth: 1 },
    dataObjectReference: { stroke: '#6c757d', fill: '#f8f9fa', strokeWidth: 1.5 },
    dataStoreReference:  { stroke: '#6c757d', fill: '#f8f9fa', strokeWidth: 1.5 },
    group:               { stroke: '#e07a5f', fill: 'none',    strokeWidth: 1.5 },
    textAnnotation:      { stroke: '#6c757d', fill: 'none',    strokeWidth: 1.5 },
    sequenceFlow:    { stroke: '#6c757d', strokeWidth: 1.5 },
    messageFlow:     { stroke: '#457b9d', strokeWidth: 1.5 },
    association:     { stroke: '#8d99ae', strokeWidth: 1 },
    dataAssociation: { stroke: '#8d99ae', strokeWidth: 1 },
    grid:   { color: '#ddd0c2' },
    canvas: { background: '#ffffff' },
    text:   { fill: '#3d405b' },
    panel: {
      background: '#fef9f3',       headerBackground: '#fdf6ec',
      headerBorder: '#e8d5c0',     text: '#3d405b',
      textSecondary: '#5c5f7a',    textMuted: '#8d99ae',
      border: '#e8d5c0',           inputBackground: '#fff',
      inputBorder: '#d4b896',      hoverBackground: '#fdf0e0',
      accentBackground: '#fceee9', accentBorder: '#f4b8a8',
      accentText: '#e07a5f',
    },
    dmn: {
      decision:               { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 2 },
      inputData:              { stroke: '#2ec4b6', fill: '#e8faf8', strokeWidth: 2 },
      businessKnowledgeModel: { stroke: '#9c6b98', fill: '#f5eaf5', strokeWidth: 2 },
      knowledgeSource:        { stroke: '#e9c46a', fill: '#fefaec', strokeWidth: 2 },
      decisionService:        { stroke: '#457b9d', fill: '#eaf2f8', strokeWidth: 2 },
      textAnnotation:         { stroke: '#6c757d', fill: 'none',    strokeWidth: 1.5 },
      informationRequirement: { stroke: '#457b9d', strokeWidth: 1.5 },
      knowledgeRequirement:   { stroke: '#9c6b98', strokeWidth: 1.5 },
      authorityRequirement:   { stroke: '#e9c46a', strokeWidth: 1.5 },
      association:            { stroke: '#8d99ae', strokeWidth: 1 },
      accent: { base: '#e07a5f', hover: '#c25a3f', bg: '#fceee9', border: '#f4b8a8', alpha: 'rgba(224,122,95,0.25)' },
      feelSyntax: {
        keyword: '#457b9d',
        literal: '#457b9d',
        number:  '#e76f51',
        string:  '#2a9d8f',
        op:      '#e63946',
        builtin: '#9c6b98',
        punct:   '#8d99ae',
      },
      chips: {
        accentBgSoft:    'rgba(224, 122, 95, 0.12)',
        accentText:      '#c25a3f',
        accentSoftBg:    'rgba(224, 122, 95, 0.15)',
        accentSoftBorder:'rgba(224, 122, 95, 0.45)',
        unknownStroke:   '#e63946',
        unknownSoft:     'rgba(230, 57, 70, 0.12)',
        unknownText:     '#a4172d',
      },
      tableGrid: {
        border: '#e8d5c0', headerBg: '#fdf6ec', indexBg: '#fef5e6', zebra: '#fdf0e0',
        inputBg:   'rgba(46, 196, 182, 0.18)',
        inputCell: 'rgba(46, 196, 182, 0.04)',
        outputBg:  'rgba(233, 196, 106, 0.20)',
        outputCell:'rgba(233, 196, 106, 0.05)',
        inputBorder: '#d4b896', inputBgField: '#fff', focusBg: '#fff9c4',
      },
    },
  },

  // ─── Pastel ───────────────────────────────────────────────────────
  // Soft, gentle pastels — ideal for presentations and light environments
  pastel: {
    startEvent:              { stroke: '#74c69d', fill: '#d8f3dc', strokeWidth: 1.5 },
    endEvent:                { stroke: '#e07a9a', fill: '#fce4ec', strokeWidth: 3 },
    intermediateEvent:       { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 1 },
    intermediateThrowEvent:  { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 1 },
    boundaryEvent:           { stroke: '#f4a261', fill: '#fde8d4', strokeWidth: 1.5 },
    userTask:         { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 1 },
    serviceTask:      { stroke: '#b09afa', fill: '#ede9ff', strokeWidth: 1 },
    scriptTask:       { stroke: '#f4a261', fill: '#fde8d4', strokeWidth: 1 },
    manualTask:       { stroke: '#74c69d', fill: '#d8f3dc', strokeWidth: 1 },
    sendTask:         { stroke: '#a8dadc', fill: '#e0f4f4', strokeWidth: 1 },
    receiveTask:      { stroke: '#90caf9', fill: '#e3f2fd', strokeWidth: 1 },
    businessRuleTask: { stroke: '#ffd166', fill: '#fff9e0', strokeWidth: 1 },
    callActivity:     { stroke: '#52b788', fill: '#d8f3dc', strokeWidth: 3 },
    exclusiveGateway:  { stroke: '#ffd166', fill: '#fff9e0', strokeWidth: 2 },
    parallelGateway:   { stroke: '#74c69d', fill: '#d8f3dc', strokeWidth: 2 },
    inclusiveGateway:  { stroke: '#e07a9a', fill: '#fce4ec', strokeWidth: 2 },
    complexGateway:    { stroke: '#9e9e9e', fill: '#f5f5f5', strokeWidth: 2 },
    eventBasedGateway: { stroke: '#52b788', fill: '#d8f3dc', strokeWidth: 2 },
    subProcess:   { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 2 },
    transaction:  { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 2 },
    participant:  { stroke: '#9e9e9e', fill: '#f0f4f8', strokeWidth: 2 },
    lane:         { stroke: '#bdbdbd', fill: '#fafafa', strokeWidth: 1 },
    dataObjectReference: { stroke: '#9e9e9e', fill: '#f5f5f5', strokeWidth: 1.5 },
    dataStoreReference:  { stroke: '#9e9e9e', fill: '#f5f5f5', strokeWidth: 1.5 },
    group:               { stroke: '#b09afa', fill: 'none',    strokeWidth: 1.5 },
    textAnnotation:      { stroke: '#9e9e9e', fill: 'none',    strokeWidth: 1.5 },
    sequenceFlow:    { stroke: '#9e9e9e', strokeWidth: 1.5 },
    messageFlow:     { stroke: '#74b3ce', strokeWidth: 1.5 },
    association:     { stroke: '#bdbdbd', strokeWidth: 1 },
    dataAssociation: { stroke: '#bdbdbd', strokeWidth: 1 },
    grid:   { color: '#d4dde8' },
    canvas: { background: '#ffffff' },
    text:   { fill: '#4a4a6a' },
    panel: {
      background: '#f8fafc',       headerBackground: '#f0f4f8',
      headerBorder: '#d1dce8',     text: '#4a4a6a',
      textSecondary: '#6b7280',    textMuted: '#9ca3af',
      border: '#d1dce8',           inputBackground: '#fff',
      inputBorder: '#b09afa',      hoverBackground: '#eef2ff',
      accentBackground: '#ede9ff', accentBorder: '#c4b5fd',
      accentText: '#7c3aed',
    },
    dmn: {
      decision:               { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 2 },
      inputData:              { stroke: '#74c69d', fill: '#d8f3dc', strokeWidth: 2 },
      businessKnowledgeModel: { stroke: '#b09afa', fill: '#ede9ff', strokeWidth: 2 },
      knowledgeSource:        { stroke: '#ffd166', fill: '#fff9e0', strokeWidth: 2 },
      decisionService:        { stroke: '#74b3ce', fill: '#dbedf7', strokeWidth: 2 },
      textAnnotation:         { stroke: '#9e9e9e', fill: 'none',    strokeWidth: 1.5 },
      informationRequirement: { stroke: '#74b3ce', strokeWidth: 1.5 },
      knowledgeRequirement:   { stroke: '#b09afa', strokeWidth: 1.5 },
      authorityRequirement:   { stroke: '#ffd166', strokeWidth: 1.5 },
      association:            { stroke: '#bdbdbd', strokeWidth: 1 },
      accent: { base: '#7c3aed', hover: '#6d28d9', bg: '#ede9ff', border: '#c4b5fd', alpha: 'rgba(124,58,237,0.25)' },
      feelSyntax: {
        keyword: '#74b3ce',
        literal: '#74b3ce',
        number:  '#f4a261',
        string:  '#74c69d',
        op:      '#e07a9a',
        builtin: '#b09afa',
        punct:   '#9e9e9e',
      },
      chips: {
        accentBgSoft:    'rgba(116, 179, 206, 0.12)',
        accentText:      '#3a7da0',
        accentSoftBg:    'rgba(116, 179, 206, 0.15)',
        accentSoftBorder:'rgba(116, 179, 206, 0.45)',
        unknownStroke:   '#e07a9a',
        unknownSoft:     'rgba(224, 122, 154, 0.12)',
        unknownText:     '#9c2858',
      },
      tableGrid: {
        border: '#cfd8e3', headerBg: '#eef2f7', indexBg: '#f0f4f8', zebra: '#f8fafc',
        inputBg:   'rgba(116, 179, 206, 0.18)',
        inputCell: 'rgba(116, 179, 206, 0.04)',
        outputBg:  'rgba(255, 209, 102, 0.20)',
        outputCell:'rgba(255, 209, 102, 0.05)',
        inputBorder: '#a8b8cc', inputBgField: '#fff', focusBg: '#fff9c4',
      },
    },
  },
};
